/**
 * **Le marché des freelances** ⟦A-4 décision 4 / arbitrage E · M3.30⟧ — le maillon 3 de
 * l'étage 1, rompu depuis M2.10.
 *
 * Le vivier tournait côté moteur, la charge utile côté API, les 25 personnes côté contenu —
 * et AUCUN composant ne les affichait : le joueur qui achetait *Le Studio* (8 PC, 2 000 ₵)
 * n'obtenait rien du tout, en silence. Cet écran ferme la rupture, et il montre d'abord ce
 * qu'⟦A-45⟧ exige de montrer : **le PRIX, avant l'embauche** — la donnée que la délégation
 * transitoire prélevait en silence à la publication.
 *
 * Ce que l'écran fait :
 *  1. montre l'offre de la fenêtre, étape par étape — cote, ± variance (la régularité qu'on
 *     paie), prix réel (`MULT_PRIX_NOTORIETE` appliqué), pépite, fidélisée ;
 *  2. laisse **embaucher** : un choix par étape, pour la prochaine vidéo. Le plan part avec
 *     le brouillon de production (`NouvelleVideo` → Atelier → `produce_video`) — le moteur
 *     ne l'apprend qu'à la publication, et c'est lui qui facture ;
 *  3. dit quand l'offre tourne (« le type d'hier n'est plus là »), et ce que TON arbre fait
 *     aux livraisons ⟦A-61⟧ — « le seul maillon faible » de l'avenant : la mécanique
 *     tournait, rien ne la montrait.
 *
 * Deux modes, une couture (`vueMarche`) : bac à sable en local sur `run.seed`, compte par
 * `GET /api/runs/:id/marche` — la graine ne sort jamais du serveur.
 *
 * ## ⟦C57 · A-174⟧ Moins de texte : des chiffres à l'écran, « Les dessous » derrière
 *
 * Matt, 02/10/2026 : *« Il y a trop de trucs écrits partout, on ne lit plus rien à la fin. »* Le
 * marché portait trois paragraphes d'explication, une description et une réplique par freelance :
 * 257 mots visibles. Il garde désormais, pour chaque offre, le nom, ses pictogrammes (🌱 🤝 🎒), la
 * cote et le prix (ce qu'⟦A-45⟧ exige de voir AVANT l'embauche) et le bouton ; l'en-tête dit « offre de
 * la semaine : 6 jours · ×3 ». Les explications du marché, la description et la réplique d'un
 * freelance, ce que veulent dire 🌱 🤝 🎒 et le devis détaillé passent derrière « Les dessous »
 * : rien n'est supprimé.
 *
 * ## ⟦C62 · lot 4 · friction 4⟧ Trois candidats, pas quinze
 *
 * Mesuré avant (partie naturelle d'un délégant, 1280 x 800) : 20 cartes (cinq étapes, quatre offres chacune une
 * fois l'offre approfondie), 2 829 px de contenu pour une fenêtre de 670, 42 boutons, 333 mots. Maintenant :
 * **une étape à la fois, ses trois candidats** (le moins cher, le milieu, le meilleur), et une rangée de cinq
 * pastilles qui dit qui est retenu pour chaque étape et qui permet d'aller de l'une à l'autre. Une embauche
 * passe à l'étape suivante qui n'a personne. « Voir les autres » ouvre l'offre entière quand elle dépasse
 * trois personnes. Le devis tient en une ligne. Un seul pictogramme « Les dessous » pour la fenêtre, dans la
 * barre de titre : la fiche de chaque freelance, ce que veulent dire les mots « pas cher », « pépite », « fidèle »,
 * et les règles du marché y sont. Rien n'est supprimé : les cotes, les prix, la direction de l'arbre, l'embauche.
 */

import { useEffect, useState } from "react";

import { H_JOUR, type AtelierStage, type OffreFreelance } from "@maxime/shared";

import { vueMarche, type VueMarche } from "../../api/marche";
import { DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { contenu } from "../../game/content";
import { ETAPES, nombre } from "../../game/format";
import { useMarcheStore } from "../../stores/marcheStore";
import { useRunStore } from "../../stores/runStore";

import { choisirCandidats } from "./candidats";

/** L'ordre d'affichage : celui du pipeline — c'est lui que le joueur connaît. */
const ORDRE: readonly AtelierStage[] = [
  "ecriture",
  "tournage",
  "montage",
  "miniature",
  "promotion",
];

/** Le nom d'une étape pour une pastille : « Miniature + Titre » devient « Miniature ». */
const nomCourt = (stage: AtelierStage): string => ETAPES[stage].nom.split(" + ")[0] ?? ETAPES[stage].nom;

export function Marche() {
  const run = useRunStore((s) => s.run);
  const plan = useMarcheStore((s) => s.plan);
  const embaucher = useMarcheStore((s) => s.embaucher);
  const reprendre = useMarcheStore((s) => s.reprendre);

  const [vue, setVue] = useState<VueMarche | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  // ⟦C62 · lot 4⟧ L'étape regardée (`null` : la première qui n'a encore personne) et celles dont on a ouvert l'offre entière.
  const [etapeVue, setEtapeVue] = useState<AtelierStage | null>(null);
  const [offreEntiere, setOffreEntiere] = useState<Partial<Record<AtelierStage, boolean>>>({});

  // L'offre dépend de l'horloge (fenêtre de 7 jours) et des embauches passées : on la relit
  // à chaque tour. `clock_h` suffit comme dépendance — le marché est une forme close.
  const clockH = run?.state.clock_h ?? 0;
  const runId = run?.runId ?? null;
  useEffect(() => {
    if (!run || runId === null) return;
    let vivant = true;
    vueMarche(run)
      .then((v) => {
        if (vivant) {
          setVue(v);
          setErreur(null);
        }
      })
      .catch((e: unknown) => {
        if (vivant) setErreur(e instanceof Error ? e.message : "Le marché n'a pas répondu.");
      });
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, clockH]);

  if (!run) return null;

  const c = contenu();
  const parId = c.parFreelance;

  if (erreur !== null) {
    return (
      <Panneau titre="🧑‍🔧 Le marché des freelances" large>
        <p className="ligne-info">{erreur}</p>
      </Panneau>
    );
  }

  if (vue === null) {
    return (
      <Panneau titre="🧑‍🔧 Le marché des freelances" large>
        <p className="ligne-info">On regarde qui est disponible cette semaine…</p>
      </Panneau>
    );
  }

  if (!vue.ouvert) {
    // ⟦A-78⟧ La porte est le Grand Saut : l'argent apparaît, le marché apparaît. Les deux
    // nœuds d'arbre ne sont plus la porte — ils élargissent l'offre.
    return (
      <Panneau titre="🧑‍🔧 Le marché des freelances" large>
        <p className="ligne-info">
          Pas encore d'argent : le marché ouvre au <strong>Grand Saut</strong>.{" "}
          <LesDessous titre="Pourquoi le marché est fermé">
            <DessousBloc>
              <p className="dessous-texte">
                Personne ne travaille pour toi — et personne ne se propose : tu n'as pas encore de
                quoi payer qui que ce soit. Le marché apparaît avec l'argent, au Grand Saut.
              </p>
            </DessousBloc>
          </LesDessous>
        </p>
      </Panneau>
    );
  }

  const etapesOuvertes = ORDRE.filter((s) => (vue.etapes[s]?.length ?? 0) > 0);
  const joursRestants = Math.max(1, Math.ceil(vue.fenetre_restante_h / H_JOUR));

  // Le devis du plan courant, aux prix de CETTE fenêtre — la vérité avant de lancer.
  const lignesPlan = etapesOuvertes
    .map((stage) => {
      const id = plan[stage];
      if (id === undefined) return null;
      const offre = vue.etapes[stage]?.find((o) => o.id === id);
      return offre === undefined ? null : { stage, offre };
    })
    .filter((l): l is { stage: AtelierStage; offre: OffreFreelance } => l !== null);
  const totalPlan = lignesPlan.reduce((t, l) => t + l.offre.prix, 0);
  const retenuDe = (stage: AtelierStage): OffreFreelance | undefined =>
    lignesPlan.find((l) => l.stage === stage)?.offre;

  // ⟦C62 · lot 4⟧ L'étape à l'écran : celle qu'on a choisie, sinon la première qui n'a encore personne.
  const premiereLibre = etapesOuvertes.find((s) => retenuDe(s) === undefined) ?? etapesOuvertes[0];
  const etape = etapeVue !== null && etapesOuvertes.includes(etapeVue) ? etapeVue : premiereLibre;

  // ⟦C62 · lot 4⟧ Le seul « ? » de la fenêtre : tout ce que l'écran ne dit plus. Les trois paragraphes d'explication
  // du marché, ce que veulent dire « pas cher », « pépite » et « fidèle », la fiche de chaque freelance de la semaine,
  // le devis : mot pour mot, rien n'est supprimé.
  const dessous = (
    <LesDessous titre="Le marché des freelances" variante="icone">
      <DessousBloc titre="Comment marche le marché">
        <p className="dessous-texte">
          L'offre de la semaine tourne dans{" "}
          <strong>
            {joursRestants} jour{joursRestants > 1 ? "s" : ""}
          </strong>
          , et le type d'hier ne sera plus là.
          {vue.mult_prix > 1 ? (
            <>
              {" "}
              À ta notoriété, tout le monde facture <strong>×{vue.mult_prix}</strong> : une
              chaîne comme la tienne ne paie plus les tarifs d'une chaîne de chambre.
            </>
          ) : null}
        </p>
        <p className="dessous-texte">
          Le prix est prélevé <strong>à chaque vidéo</strong>, à la publication — un freelance,
          ça s'entretient, ça ne se déverrouille pas. Et déléguer coûte de la qualité :
          personne ici ne vaut ton meilleur geste.
        </p>
        {/* ⟦A-61⟧ « L'écran dit au joueur que son arbre améliore la livraison. » */}
        <p className="dessous-texte">
          🎯 <strong>Tu sais ce que tu demandes</strong> : ton arbre fait parcourir aux
          livraisons d'une étape un pourcentage du chemin vers ce que ta chaîne sait exploiter
          (le chiffre est écrit à côté du nom de l'étape).
        </p>
      </DessousBloc>
      <DessousBloc titre="Ce que veulent dire les mots">
        <DessousLignes
          lignes={[
            // ⟦A-86⟧ Le tarif d'entrée : ce qui fait que le premier marché ne ruine personne.
            { icone: "🎒", texte: "Pas cher : le tarif d'entrée. Sur chaque étape, quelqu'un est toujours là, pas cher. Une vidéo moyenne, jamais une bonne. Les autres tournent, lui reste." },
            { icone: "🌱", texte: "Pépite : un pari de confiance. Sa cote monte à chaque embauche, et une fois embauchée elle ne quitte plus le marché." },
            { icone: "🤝", texte: "Fidèle : elle est là parce que tu l'as fidélisée, pas par le tirage." },
            { icone: "🎯", texte: "Qualité : la note sur 100 de ce qu'elle rend. Le « ± » dit de combien elle varie d'une vidéo à l'autre." },
          ]}
        />
      </DessousBloc>
      {etapesOuvertes.map((stage) => (
        <DessousBloc key={stage} titre={`${ETAPES[stage].icone} ${ETAPES[stage].nom}`}>
          <DessousLignes
            lignes={(vue.etapes[stage] ?? []).map((o) => {
              const fiche = parId.get(o.id);
              return {
                texte: (
                  <>
                    <strong>{o.nom}</strong> : {fiche ? `${fiche.description} « ${fiche.phrase} »` : "pas de fiche"} (
                    {o.cote} ± {o.variance}, {nombre(o.prix)} ₵
                    {o.embauches > 0 ? `, déjà travaillé ensemble ×${o.embauches}` : ""})
                  </>
                ),
              };
            })}
          />
        </DessousBloc>
      ))}
      <DessousBloc titre="Le devis">
        <p className="dessous-texte">
          Le devis total compte les étapes retenues (un short saute la miniature et la
          promo).
        </p>
        <p className="dessous-texte">
          Rien n'est débité maintenant : tu paies à la publication, aux prix affichés — et si
          la caisse ne suit pas, la vidéo ne se fait pas. Tu paies tout le monde, ou tu
          reprends les ciseaux.
        </p>
      </DessousBloc>
    </LesDessous>
  );

  if (etape === undefined) {
    return (
      <Panneau titre="🧑‍🔧 Le marché des freelances" large actions={dessous}>
        <p className="ligne-info">Personne ne se propose cette semaine.</p>
      </Panneau>
    );
  }

  const offres = vue.etapes[etape] ?? [];
  const direction = vue.direction[etape] ?? 0;
  const retenuId = plan[etape];
  const toutVoir = offreEntiere[etape] === true;
  const montrees = toutVoir ? offres : choisirCandidats(offres, retenuId);
  const cachees = offres.length - montrees.length;

  /** Embaucher pour une étape qui n'avait encore personne passe à la suivante qui n'a personne ; changer d'avis, non. */
  function embaucherPour(stage: AtelierStage, id: string) {
    const premiere = plan[stage] === undefined;
    embaucher(stage, id);
    if (!premiere) return;
    const suivante = etapesOuvertes.find((s) => s !== stage && plan[s] === undefined);
    if (suivante !== undefined) setEtapeVue(suivante);
  }

  return (
    <Panneau titre="🧑‍🔧 Le marché des freelances" large actions={dessous}>
      {/* ⟦C57 · A-174⟧ L'en-tête : deux chiffres. */}
      <p className="ligne-info">
        Offre de la semaine : <strong>{joursRestants} j</strong>
        {vue.mult_prix > 1 ? (
          <>
            {" "}
            · prix <strong>×{vue.mult_prix}</strong>
          </>
        ) : null}
      </p>

      {/* ⟦C62 · lot 4⟧ Cinq pastilles : l'étape regardée, et qui est retenu pour chacune (« toi » = tu la joues). */}
      <div className="marche-pastilles" role="group" aria-label="Les étapes de la vidéo">
        {etapesOuvertes.map((stage) => {
          const pourvu = retenuDe(stage);
          return (
            <button
              key={stage}
              type="button"
              className={`marche-pastille${stage === etape ? " active" : ""}${pourvu ? " pourvue" : ""}`}
              aria-pressed={stage === etape}
              title={pourvu ? `${pourvu.nom} joue cette étape` : "Tu joues cette étape toi-même"}
              onClick={() => setEtapeVue(stage)}
            >
              <span className="marche-pastille-etape">
                {ETAPES[stage].icone} {nomCourt(stage)}
              </span>
              <span className="marche-pastille-qui">{pourvu ? pourvu.nom : "toi"}</span>
            </button>
          );
        })}
      </div>

      <section className="marche-etape">
        <h3>
          {ETAPES[etape].icone} {ETAPES[etape].nom}
          {/* ⟦A-61⟧ « L'écran dit au joueur que son arbre améliore la livraison. » : un chiffre ;
              la phrase est dans les dessous du marché. */}
          {direction > 0 ? (
            <span className="marche-direction" title="Direction : le chemin que ton arbre fait parcourir aux livraisons">
              {" "}
              🎯 {Math.round(direction * 100)} %
            </span>
          ) : null}
        </h3>
        <p className="ligne-info">Garde l'étape, ou choisis qui la joue à ta place.</p>
        <div className="marche-offres">
          {montrees.map((o) => {
            const retenu = retenuId === o.id;
            return (
              <article key={o.id} className={`carte marche-offre ${retenu ? "choisi" : ""}`}>
                <h4>{o.nom}</h4>
                {o.tarif_entree || o.pepite || o.fidelise ? (
                  <p className="marche-mots">
                    {o.tarif_entree ? (
                      <span title="Toujours là, pas cher : une vidéo moyenne, jamais une bonne.">Pas cher</span>
                    ) : null}
                    {o.pepite ? (
                      <span title="Un pari de confiance : sa cote monte à chaque embauche.">Pépite</span>
                    ) : null}
                    {o.fidelise ? <span title="Elle est là parce que tu l'as fidélisée.">Fidèle</span> : null}
                  </p>
                ) : null}
                <ul className="recap">
                  <li title={`Elle rend ${o.cote} sur 100, et varie de ${o.variance} points d'une vidéo à l'autre.`}>
                    <span>Qualité sur 100</span>
                    <strong>
                      {o.cote} ± {o.variance}
                    </strong>
                  </li>
                  <li title="Par vidéo, prélevé à la publication.">
                    <span>Prix</span>
                    <strong>{nombre(o.prix)} ₵</strong>
                  </li>
                  {o.embauches > 0 ? (
                    <li>
                      <span>Ensemble</span>
                      <strong>×{o.embauches}</strong>
                    </li>
                  ) : null}
                </ul>
                <button
                  type="button"
                  className={retenu ? "bouton" : "bouton discret"}
                  title={retenu ? "Reprendre l'étape" : "Pour la prochaine vidéo"}
                  onClick={() => (retenu ? reprendre(etape) : embaucherPour(etape, o.id))}
                >
                  {retenu ? "✓ Dans l'équipe" : "Embaucher"}
                </button>
              </article>
            );
          })}
        </div>
        {cachees > 0 || toutVoir ? (
          <button
            type="button"
            className="bouton discret marche-autres"
            onClick={() => setOffreEntiere({ ...offreEntiere, [etape]: !toutVoir })}
          >
            {toutVoir ? "Revenir aux meilleurs" : cachees === 1 ? "Voir l'autre" : `Voir les ${cachees} autres`}
          </button>
        ) : null}
      </section>

      {/* ⟦C62 · lot 4⟧ Le devis tient en une ligne ; les lignes par étape sont dans les pastilles. */}
      {lignesPlan.length > 0 ? (
        <p className="ligne-info marche-devis">
          Devis de la prochaine vidéo : <strong>{nombre(totalPlan)} ₵</strong>, prélevé à la publication.
        </p>
      ) : null}
    </Panneau>
  );
}
